import moment from "moment";
import {useNavigate} from "react-router-dom";

import Page from "@/components/Page";
import {
	DsEmpty,
	DsForm,
	DsHeader,
	DsKpi,
	DsKpiRow,
	DsLink,
	DsMissingSources,
	DsResultWarnings,
	DsSection,
	DsTable,
	DsSkeleton,
} from "@/components/DsKit";

import {matchDateLabel, dsMatchCounts} from "@/utils/clubSnapshot";
import {useClubSnapshot} from "@/utils/hooks";

const competitionOf = (m) => m.competitionName || m.matchType || "";

function DsMatchesPage(props) {
	const {handleNotification} = props;
	const navigate = useNavigate();
	const {
		data: s,
		loading,
		reload,
		scope,
	} = useClubSnapshot({handleNotification});

	const now = moment();
	const teams = s?.teams || [];

	const upcoming = teams
		.flatMap((t) =>
			t.upcoming
				.filter((m) =>
					moment(m.startDate).isBefore(now.clone().add(14, "days")),
				)
				.map((m) => ({...m, team: t})),
		)
		.sort((a, b) => new Date(a.startDate) - new Date(b.startDate));

	const results = teams
		.flatMap((t) =>
			t.played
				.filter((m) =>
					moment(m.startDate).isAfter(now.clone().subtract(14, "days")),
				)
				.map((m) => ({...m, team: t})),
		)
		.sort((a, b) => new Date(b.startDate) - new Date(a.startDate));

	const totals = teams.reduce(
		(acc, t) => {
			["V", "N", "P", "gf", "ga"].forEach((k) => (acc[k] += t.record[k]));
			return acc;
		},
		{V: 0, N: 0, P: 0, gf: 0, ga: 0},
	);
	const playedCount = totals.V + totals.N + totals.P;
	const matchCounts = dsMatchCounts(teams);

	const standingsColumns = [
		{key: "name", label: "Squadra", render: (t) => <b>{t.name}</b>},
		{
			key: "played",
			label: "Giocate",
			align: "center",
			render: (t) => t.record.V + t.record.N + t.record.P,
		},
		{key: "V", label: "Vinte", align: "center", render: (t) => t.record.V},
		{key: "N", label: "Pari", align: "center", render: (t) => t.record.N},
		{key: "P", label: "Perse", align: "center", render: (t) => t.record.P},
		{
			key: "goals",
			label: "Gol",
			align: "center",
			render: (t) => `${t.record.gf}:${t.record.ga}`,
		},
		{
			key: "ppg",
			label: "Punti/gara",
			align: "center",
			render: (t) => {
				const g = t.record.V + t.record.N + t.record.P;
				return g ? ((t.record.V * 3 + t.record.N) / g).toFixed(2) : "—";
			},
		},
		{key: "form", label: "Ultime 5", render: (t) => <DsForm form={t.form} />},
	];

	return (
		<Page>
			<div className="ds">
				<DsHeader
					title="Gare"
					subtitle="Calendario e risultati di tutte le squadre"
					snapshot={s}
					scope={scope}
					area="gare"
					onReload={reload}
					reloading={loading}
					actions={
						<button
							className="ds-btn ds-btn--ghost"
							onClick={() => navigate("/calendar")}
						>
							Calendario completo
						</button>
					}
				/>
				<DsSkeleton visible={loading && !s} />
				{s && (
					<>
						<DsMissingSources sources={s.missingSources} />
						<DsResultWarnings teams={s.teams} />
						<DsKpiRow>
							<DsKpi
								label="Gare uniche disputate"
								value={matchCounts.uniquePlayed}
								sub={playedCount + " partecipazioni delle rose in stagione"}
							/>
							<DsKpi
								label="Vittorie delle rose"
								value={
									playedCount
										? Math.round((totals.V / playedCount) * 100) + "%"
										: "—"
								}
								sub={`${totals.V}V · ${totals.N}N · ${totals.P}P · partecipazioni`}
							/>
							<DsKpi
								label="Reti delle partecipazioni"
								value={
									(totals.gf - totals.ga > 0 ? "+" : "") +
									(totals.gf - totals.ga)
								}
								sub={`${totals.gf} fatti · ${totals.ga} subiti`}
							/>
							<DsKpi
								label="In programma"
								value={new Set(upcoming.map((match) => match.id)).size}
								sub="prossimi 14 giorni"
							/>
						</DsKpiRow>

						<div className="ds-two-cols">
							<DsSection
								title="In programma"
								hint="Prossimi 14 giorni · una voce per ogni rosa in gara"
							>
								{upcoming.length ? (
									<div className="ds-list">
										{upcoming.map((m) => (
											<div
												key={m.id + m.team.id}
												className="ds-list__item is-clickable"
												onClick={() => navigate("/ds/gara/" + m.id)}
											>
												<div className="ds-date">
													<span className="ds-date__day">
														{moment(m.startDate).format("D")}
													</span>
													<span className="ds-date__month">
														{moment(m.startDate).format("MMM")}
													</span>
												</div>
												<div className="ds-list__main">
													<div className="ds-list__title">
														{m.homeClub} — {m.awayClub}
													</div>
													<div className="ds-list__sub">
														{m.team.name}
														{competitionOf(m) && " · " + competitionOf(m)}
													</div>
												</div>
												<div className="ds-list__aside">
													{matchDateLabel(m, "ddd")}
												</div>
											</div>
										))}
									</div>
								) : (
									<DsEmpty>Nessuna gara in programma.</DsEmpty>
								)}
							</DsSection>

							<DsSection
								title="Risultati"
								hint="Ultimi 14 giorni · una voce per ogni rosa in gara"
							>
								{results.length ? (
									<div className="ds-list">
										{results.map((m) => (
											<div
												key={m.id + m.team.id}
												className="ds-list__item is-clickable"
												onClick={() => navigate("/ds/gara/" + m.id)}
											>
												<DsForm form={[m.result]} />
												<div className="ds-list__main">
													<div className="ds-list__title">
														{m.team.name} {m.home ? "vs" : "@"} {m.opponent}
													</div>
													<div className="ds-list__sub">
														{moment(m.startDate).format("ddd D/MM")}
														{competitionOf(m) && " · " + competitionOf(m)}
													</div>
												</div>
												<div className="ds-list__aside">
													{m.gf} – {m.ga}
												</div>
											</div>
										))}
									</div>
								) : (
									<DsEmpty>Nessun risultato negli ultimi 14 giorni.</DsEmpty>
								)}
							</DsSection>
						</div>

						<DsSection
							title="Rendimento per squadra"
							hint="Stagione in corso"
							action={<DsLink to="/competitions">Competizioni</DsLink>}
						>
							<DsTable
								columns={standingsColumns}
								rows={teams}
								onRowClick={(t) => navigate("/ds/squadra/" + t.id)}
							/>
						</DsSection>
					</>
				)}
			</div>
		</Page>
	);
}

export default DsMatchesPage;
